import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { useSetSelectTheme } from "@/contexts/SelectThemeContext";
import { toast } from "sonner";
import { ArrowLeft, Plus, Minus, Trash2 } from "lucide-react";
import { formatDose } from "@/lib/formatDose";

/**
 * Build Your Own Kit — Select tier only.
 *
 * UX:
 *  · Kit size selector (3 vials → 10% off, 5 vials → 20% off)
 *  · Left grid of BYOK-eligible vials (products the admin flagged; products
 *    with mg-variants explode into one card per variant)
 *  · Right rail: running kit summary + server-authoritative quote + Add-to-cart
 *
 * Pricing is ALWAYS re-quoted server-side via POST /byok/quote whenever the
 * kit reaches the required vial count. Client-side arithmetic is only for the
 * visual "estimate" bar; the authoritative kit price comes from the server.
 */
export default function BuildAKit() {
  const nav = useNavigate();
  const { addByokKit } = useCart();
  useSetSelectTheme(true);

  const [loading, setLoading] = useState(true);
  const [options, setOptions] = useState([]);
  const [discountLadder, setDiscountLadder] = useState({ 3: 0.1, 5: 0.2 });
  const [size, setSize] = useState(3);
  // picks: array of { key, product_id, variant_mg, name, price, image_url }
  const [picks, setPicks] = useState([]);
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/byok/options");
        setOptions(data.options || []);
        setDiscountLadder(data.discount_ladder || { 3: 0.1, 5: 0.2 });
      } catch (err) {
        toast.error("Couldn't load the kit builder. Try again in a moment.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Re-quote from the server whenever the kit is complete.
  useEffect(() => {
    if (picks.length !== size) {
      setQuote(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setQuoting(true);
      try {
        const { data } = await api.post("/byok/quote", {
          size,
          picks: picks.map((p) => ({ product_id: p.product_id, variant_mg: p.variant_mg })),
        });
        if (!cancelled) setQuote(data);
      } catch (err) {
        if (!cancelled) toast.error(err?.response?.data?.detail || "Couldn't price this kit — swap a vial and try again.");
      } finally {
        if (!cancelled) setQuoting(false);
      }
    })();
    return () => { cancelled = true; };
  }, [picks, size]);

  const clientEstimate = useMemo(() => {
    const subtotal = picks.reduce((s, p) => s + Number(p.price || 0), 0);
    const rate = discountLadder[size] ?? 0;
    return {
      subtotal,
      discount: subtotal * rate,
      kit_price: subtotal * (1 - rate),
    };
  }, [picks, size, discountLadder]);

  const addPick = (opt) => {
    if (picks.length >= size) {
      toast.info(`Your ${size}-vial kit is full. Remove a vial before adding another.`);
      return;
    }
    if (!opt.in_stock) {
      toast.error(`${opt.name} is out of stock right now.`);
      return;
    }
    // single_buy hard cap (e.g., Bacteriostatic Water). Refuse a second copy
    // of the same product regardless of mg variant. Server-side enforces the
    // same rule at /byok/quote — this is just the friendly client guard.
    if (opt.single_buy && picks.some((p) => p.product_id === opt.product_id)) {
      toast.error(`${opt.name} is limited to one vial per kit — supply is tight.`);
      return;
    }
    setPicks((prev) => [...prev, opt]);
  };

  const removePickAtIndex = (idx) => {
    setPicks((prev) => prev.filter((_, i) => i !== idx));
  };

  const changeSize = (next) => {
    if (next === size) return;
    if (picks.length > next) {
      // Trim to fit new size
      setPicks((prev) => prev.slice(0, next));
    }
    setSize(next);
  };

  const finishAdd = () => {
    if (!quote) return;
    addByokKit({
      size: quote.size,
      subtotal: quote.subtotal,
      discount_rate: quote.discount_rate,
      discount_amount: quote.discount_amount,
      kit_price: quote.kit_price,
      items: quote.items,
    });
    toast.success(`Custom ${quote.size}-vial kit added to cart — you saved $${quote.discount_amount.toFixed(2)}.`);
    nav("/cart");
  };

  // Group picks by key for the "N in kit" chips on the option cards.
  const pickCountByKey = useMemo(() => {
    const m = {};
    for (const p of picks) m[p.key] = (m[p.key] || 0) + 1;
    return m;
  }, [picks]);

  // Consolidate flat options[] into one entry per product. Each group carries
  // its variants[] for the multi-variant modal picker. Single-variant products
  // render as a "click-to-add" card; multi-variant products render as
  // "click-to-open-picker" cards so we never end up with 6 Retatrutide rows on
  // the BYOK page — you get ONE Retatrutide card that opens a mg picker.
  const productGroups = useMemo(() => {
    const groups = new Map();
    for (const opt of options) {
      const g = groups.get(opt.product_id);
      if (g) {
        g.variants.push(opt);
      } else {
        groups.set(opt.product_id, {
          product_id: opt.product_id,
          slug: opt.slug,
          name: opt.name,
          category: opt.category,
          image_url: opt.image_url,
          variants: [opt],
        });
      }
    }
    return Array.from(groups.values());
  }, [options]);

  // How many picks in the kit come from this product (across all its mg variants)
  const pickCountByProduct = useMemo(() => {
    const m = {};
    for (const p of picks) m[p.product_id] = (m[p.product_id] || 0) + 1;
    return m;
  }, [picks]);

  // Modal state — non-null when the buyer opened a multi-variant product's picker.
  const [activeGroup, setActiveGroup] = useState(null);

  const remaining = Math.max(0, size - picks.length);
  const percentOff = Math.round((discountLadder[size] || 0) * 100);

  return (
    <div className="max-w-[1200px] mx-auto px-6 lg:px-12 py-10 lg:py-14" data-testid="byok-page">
      <Link
        to="/shop"
        className="text-[13px] uppercase tracking-[0.22em] inline-flex items-center gap-2 mb-8 text-[#6E521A] hover:text-[#6E521A] transition-colors"
        data-testid="byok-back-to-catalog"
      >
        <ArrowLeft size={12} strokeWidth={1.5}/> Back to catalog
      </Link>

      <div className="mb-10">
        <p className="text-[12px] tracking-[0.28em] uppercase text-[#6E521A] mb-3">Research 1617 · Custom Kit</p>
        <h1 className="font-serif text-4xl md:text-5xl tracking-tight text-[#1B1614] mb-3">Build Your Own Research Kit</h1>
        <p className="text-sm text-[#6E521A] max-w-2xl">
          Mix and match compatible compounds into a custom 3- or 5-vial kit. 3-vial kits save 10%, 5-vial kits save 20%. Shipping and 1-kit minimum follow standard Research 1617 rules.
        </p>
      </div>

      {/* Kit size selector */}
      <div className="mb-10 flex flex-wrap items-center gap-3" data-testid="byok-size-picker">
        <span className="text-[12px] tracking-[0.28em] uppercase text-[#6E521A] mr-2">Kit size</span>
        {[3, 5].map((n) => {
          const off = Math.round((discountLadder[n] || 0) * 100);
          const active = n === size;
          return (
            <button
              key={n}
              type="button"
              onClick={() => changeSize(n)}
              className={`text-xs uppercase tracking-[0.22em] px-5 py-3 rounded-full border transition-all ${active ? "bg-[#DDA94E] border-[#DDA94E] text-[#1A0A0D] font-semibold" : "border-[#B08A45]/40 text-[#6E521A] hover:border-[#DDA94E]"}`}
              data-testid={`byok-size-${n}`}
            >
              {n} vials · {off}% off
            </button>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-[1fr_360px] gap-8 lg:gap-12 items-start">
        {/* LEFT: options grid */}
        <div className="min-w-0">
          <h2 className="font-serif text-2xl text-[#1B1614] mb-1">Choose your compounds</h2>
          <p className="text-xs text-[#6E521A] mb-6">Every vial below is Research 1617-tier, COA-backed, and eligible for the kit builder.</p>

          {loading ? (
            <p className="text-[#6E521A] text-sm">Loading options…</p>
          ) : productGroups.length === 0 ? (
            <div className="border border-[#D6C8A3] bg-[#EBE0BE] rounded-2xl p-8 text-center">
              <p className="text-[#1B1614] font-serif text-lg mb-1">No BYOK-eligible vials yet.</p>
              <p className="text-[#6E521A] text-sm">Admin can flag Select products as "BYOK eligible" from the product form.</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4">
              {productGroups.map((g) => {
                const single = g.variants.length === 1;
                const opt = single ? g.variants[0] : null;
                const productCount = pickCountByProduct[g.product_id] || 0;
                // Card availability: for single-variant, mirror the variant's stock.
                // For multi-variant, "available" if ANY variant is in stock.
                const anyInStock = g.variants.some((v) => v.in_stock);
                const kitFull = picks.length >= size && productCount === 0;
                const disabled = single ? (!opt.in_stock || kitFull) : (!anyInStock || kitFull);
                // Starting price = min across in-stock variants (falls back to any).
                const priceable = g.variants.filter((v) => v.in_stock);
                const startingPrice = Math.min(...(priceable.length ? priceable : g.variants).map((v) => Number(v.price)));
                const count = single ? (pickCountByKey[opt.key] || 0) : productCount;
                return (
                  <div
                    key={g.product_id}
                    className={`relative border rounded-2xl p-4 flex gap-4 transition-all ${count > 0 ? "border-2 border-[#DDA94E] bg-[#F0D98E] shadow-[0_2px_16px_-4px_rgba(221,169,78,0.5)]" : "border-[#D6C8A3] bg-[#EBE0BE] hover:border-[#DDA94E]/70 hover:-translate-y-[1px] hover:shadow-[0_4px_20px_-8px_rgba(221,169,78,0.35)]"} ${!anyInStock ? "opacity-60" : ""} ${!single ? "cursor-pointer" : ""}`}
                    onClick={!single ? () => setActiveGroup(g) : undefined}
                    role={!single ? "button" : undefined}
                    tabIndex={!single ? 0 : undefined}
                    onKeyDown={!single ? (e) => (e.key === "Enter" || e.key === " ") && setActiveGroup(g) : undefined}
                    data-testid={`byok-product-${g.product_id}`}
                  >
                    {/* iter_142 — thumbnail image removed. At 64×64 the vial
                        shots were unreadable and indistinguishable across
                        compounds; the compound name + strength does the work. */}
                    <div className="flex-1 min-w-0">
                      <p className="font-serif text-[#1B1614] text-base leading-tight truncate">{g.name}</p>
                      <p className="text-[12px] tracking-[0.2em] uppercase text-[#6E521A] mb-1">
                        {single ? (opt.variant_mg ? formatDose(opt.variant_mg, g.variant_unit) : "Single vial") : `${g.variants.length} strengths`}
                      </p>
                      <p className="font-mono text-[#6E521A] text-sm mb-2">
                        {single ? (
                          <>${Number(opt.price).toFixed(2)} <span className="text-[12px] text-[#6E521A]">/ vial</span></>
                        ) : (
                          <><span className="text-[12px] text-[#6E521A]">from</span> ${startingPrice.toFixed(2)}</>
                        )}
                      </p>
                      {single && count > 0 ? (
                        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            className="w-7 h-7 rounded-full border border-[#DDA94E] text-[#6E521A] hover:bg-[#DDA94E] hover:text-[#1A0A0D] transition-colors flex items-center justify-center"
                            onClick={() => {
                              const idx = picks.findIndex((p) => p.key === opt.key);
                              if (idx > -1) removePickAtIndex(idx);
                            }}
                            data-testid={`byok-option-decrement-${opt.key}`}
                            aria-label="Remove one"
                          >
                            <Minus size={12} strokeWidth={2}/>
                          </button>
                          <span className="text-[13px] uppercase tracking-[0.2em] text-[#6E521A]">{count} in kit</span>
                          <button
                            type="button"
                            className="w-7 h-7 rounded-full border border-[#DDA94E] text-[#6E521A] hover:bg-[#DDA94E] hover:text-[#1A0A0D] transition-colors flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed"
                            onClick={() => addPick(opt)}
                            disabled={picks.length >= size || (opt.single_buy && count >= 1)}
                            title={opt.single_buy && count >= 1 ? "Limited to one vial per kit" : undefined}
                            data-testid={`byok-option-increment-${opt.key}`}
                            aria-label="Add another"
                          >
                            <Plus size={12} strokeWidth={2}/>
                          </button>
                        </div>
                      ) : single ? (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); addPick(opt); }}
                          disabled={disabled}
                          className="text-[12px] uppercase tracking-[0.22em] px-4 py-2 rounded-full border border-[#DDA94E] text-[#6E521A] hover:bg-[#DDA94E] hover:text-[#1A0A0D] transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
                          data-testid={`byok-option-add-${opt.key}`}
                        >
                          <Plus size={12} strokeWidth={2}/>
                          {opt.in_stock ? "Add to kit" : "Out of stock"}
                        </button>
                      ) : (
                        // Multi-variant summary chip — the whole card is clickable to open the picker.
                        <div className="flex items-center gap-3">
                          <span className="text-[12px] uppercase tracking-[0.22em] px-3 py-1.5 rounded-full border border-[#DDA94E]/60 text-[#6E521A]">
                            {count > 0 ? `${count} in kit · choose more` : "Choose strength →"}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Multi-variant strength picker modal. Stays open on Add so the buyer
            can stack multiple mg strengths of the same product in one visit
            (e.g. 1x 10mg + 1x 20mg Retatrutide). "Done" or backdrop click closes. */}
        {activeGroup && (
          <ByokVariantPickerModal
            group={activeGroup}
            picks={picks}
            size={size}
            pickCountByKey={pickCountByKey}
            onAdd={addPick}
            onRemove={removePickAtIndex}
            onClose={() => setActiveGroup(null)}
          />
        )}

        {/* RIGHT: kit summary rail */}
        <div className="border border-[#D6C8A3] bg-[#EBE0BE] rounded-2xl p-6 lg:sticky lg:top-6" data-testid="byok-summary">
          <div className="flex items-baseline justify-between mb-4">
            <h3 className="font-serif text-xl text-[#1B1614]">Your kit</h3>
            <p className="text-xs text-[#6E521A]"><span className="text-[#6E521A] font-semibold">{picks.length}</span> / {size} vials</p>
          </div>

          {picks.length === 0 ? (
            <p className="text-sm text-[#6E521A] pb-6 border-b border-[#D6C8A3] mb-4">
              No vials added yet. Choose compounds from the list to build your kit.
            </p>
          ) : (
            <ul className="space-y-2 pb-4 border-b border-[#D6C8A3] mb-4" data-testid="byok-summary-list">
              {picks.map((p, idx) => (
                <li key={`${p.key}-${idx}`} className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm text-[#1B1614] truncate">
                      1× {p.name}{p.variant_mg ? ` ${formatDose(p.variant_mg, p.variant_unit)}` : ""}
                    </p>
                    <p className="text-[13px] font-mono text-[#6E521A]">${Number(p.price).toFixed(2)}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => removePickAtIndex(idx)}
                    className="text-oxblood/80 hover:text-oxblood transition-colors shrink-0"
                    data-testid={`byok-summary-remove-${idx}`}
                    aria-label="Remove vial"
                  >
                    <Trash2 size={14} strokeWidth={1.5}/>
                  </button>
                </li>
              ))}
            </ul>
          )}

          {/* Price block */}
          <div className="space-y-1.5 mb-5">
            <div className="flex items-center justify-between text-sm">
              <span className="text-[#6E521A]">Regular</span>
              <span className="font-mono text-[#6E521A] line-through">${(quote?.subtotal ?? clientEstimate.subtotal).toFixed(2)}</span>
            </div>
            <div className="flex items-baseline justify-between">
              <span className="text-[#1B1614] font-serif text-lg">Kit price</span>
              <span className="font-mono text-[#6E521A] text-2xl font-semibold">
                ${(quote?.kit_price ?? clientEstimate.kit_price).toFixed(2)}
              </span>
            </div>
            {(quote || picks.length > 0) && (
              <p className="text-[13px] text-[#6E521A]/90 font-mono">
                You save ${(quote?.discount_amount ?? clientEstimate.discount).toFixed(2)} ({percentOff}% off)
              </p>
            )}
          </div>

          <button
            type="button"
            onClick={finishAdd}
            disabled={!quote || quoting}
            className="w-full py-3 rounded-full bg-[#DDA94E] text-[#1A0A0D] uppercase tracking-[0.22em] text-[13px] font-semibold hover:bg-[#F1E5C7] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="byok-add-to-cart"
          >
            {quoting ? "Pricing…" : "Add to cart"}
          </button>
          <p className="text-[13px] text-[#6E521A] mt-3 text-center">
            {picks.length === 0
              ? `Add ${size} vials to complete this kit.`
              : remaining > 0
                ? `Add ${remaining} more vial${remaining === 1 ? "" : "s"} to complete this kit.`
                : "Ready to add to cart."}
          </p>
        </div>
      </div>
    </div>
  );
}


/**
 * ByokVariantPickerModal — opened when the buyer clicks a multi-variant product
 * card. Shows a pill for each mg strength; clicking Add-to-kit stacks it into
 * the running kit WITHOUT closing the modal (mode "c"). Done button or backdrop
 * click dismisses. Escape key also closes.
 */
function ByokVariantPickerModal({ group, picks, size, pickCountByKey, onAdd, onRemove, onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  const kitFull = picks.length >= size;
  const inKitFromThisProduct = picks.filter((p) => p.product_id === group.product_id).length;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label={`Choose strength for ${group.name}`}
      onClick={onClose}
      data-testid="byok-variant-modal"
    >
      <div
        className="relative w-full max-w-lg border border-[#DDA94E]/30 bg-[#F1E5C7] rounded-2xl p-6 lg:p-8 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 w-8 h-8 rounded-full border border-[#B08A45]/40 text-[#6E521A] hover:text-[#6E521A] hover:border-[#DDA94E] transition-colors flex items-center justify-center"
          aria-label="Close"
          data-testid="byok-variant-modal-close"
        >
          ×
        </button>
        <p className="text-[12px] tracking-[0.28em] uppercase text-[#6E521A] mb-2">Choose strength</p>
        <h3 className="font-serif text-2xl text-[#1B1614] mb-1">{group.name}</h3>
        <p className="text-xs text-[#6E521A] mb-5">
          Pick any mg to add it to your kit. You can stack more than one strength.
          {inKitFromThisProduct > 0 && <> Currently in kit: <span className="text-[#6E521A]">{inKitFromThisProduct}</span></>}
        </p>
        <div className="space-y-2 mb-5">
          {group.variants.map((opt) => {
            const count = pickCountByKey[opt.key] || 0;
            const disabled = !opt.in_stock || (kitFull && count === 0);
            return (
              <div
                key={opt.key}
                className={`flex items-center justify-between gap-3 border rounded-xl px-4 py-3 transition-colors ${count > 0 ? "border-2 border-[#DDA94E] bg-[#F0D98E]" : "border-[#D6C8A3] bg-[#EBE0BE]"} ${!opt.in_stock ? "opacity-60" : ""}`}
                data-testid={`byok-modal-row-${opt.key}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] uppercase tracking-[0.22em] text-[#1B1614] font-mono">
                    {opt.variant_mg ? formatDose(opt.variant_mg, group.variant_unit) : "Single vial"}
                  </p>
                  <p className="font-mono text-[#6E521A] text-sm mt-0.5">
                    ${Number(opt.price).toFixed(2)}
                    <span className="text-[12px] text-[#6E521A] ml-1">/ vial</span>
                  </p>
                </div>
                {count > 0 ? (
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      className="w-8 h-8 rounded-full border border-[#DDA94E] text-[#6E521A] hover:bg-[#DDA94E] hover:text-[#1A0A0D] transition-colors flex items-center justify-center"
                      onClick={() => {
                        const idx = picks.findIndex((p) => p.key === opt.key);
                        if (idx > -1) onRemove(idx);
                      }}
                      data-testid={`byok-modal-decrement-${opt.key}`}
                      aria-label="Remove one"
                    >
                      <Minus size={12} strokeWidth={2}/>
                    </button>
                    <span className="text-[13px] uppercase tracking-[0.2em] text-[#6E521A] min-w-[54px] text-center">{count} in kit</span>
                    <button
                      type="button"
                      className="w-8 h-8 rounded-full border border-[#DDA94E] text-[#6E521A] hover:bg-[#DDA94E] hover:text-[#1A0A0D] transition-colors flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed"
                      onClick={() => onAdd(opt)}
                      disabled={kitFull}
                      data-testid={`byok-modal-increment-${opt.key}`}
                      aria-label="Add another"
                    >
                      <Plus size={12} strokeWidth={2}/>
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => onAdd(opt)}
                    disabled={disabled}
                    className="text-[12px] uppercase tracking-[0.22em] px-4 py-2 rounded-full border border-[#DDA94E] text-[#6E521A] hover:bg-[#DDA94E] hover:text-[#1A0A0D] transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
                    data-testid={`byok-modal-add-${opt.key}`}
                  >
                    <Plus size={12} strokeWidth={2}/>
                    {opt.in_stock ? "Add to kit" : "Out of stock"}
                  </button>
                )}
              </div>
            );
          })}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="w-full py-3 rounded-full bg-[#DDA94E] hover:bg-[#F1CC70] text-[#1A0A0D] text-xs uppercase tracking-[0.28em] font-semibold transition-colors"
          data-testid="byok-modal-done"
        >
          Done
        </button>
      </div>
    </div>
  );
}
